import {
  batchesForResultSheetQuery,
  batchSubjectConfigurationForResultSheetQuery,
  batchSubjectConfigurationForResultSheetQueryVariables,
  BATCH_SUBJECT_CONFIGURATION_FOR_RESULT_SHEET,
} from '@app-types/graphql-types';
import React, { useEffect, useState } from 'react';
import { Button, Divider, Form as AntForm, Input, Modal, Table, Tag } from 'antd';
import { approveDenyResultSheet, getStudentResultWithGrade, StudentResults } from '@modules/resultSheet';
import { useMst } from '@models/Root';
import { toJS } from 'mobx';
import { StudentResultSheetModelType } from '@models/StudentResultSheet';
import { submitResultSheet } from '@modules/resultSheet/functions/submitResultSheet';
import { useToasts } from 'react-toast-notifications';
import { useRouter } from 'next/router';
import { useQuery } from '@apollo/client';
import { observer } from 'mobx-react';

const { TextArea } = Input;
const { Item: FormItem } = AntForm;

interface ResultSheetStudentsProps {
  selectedBatchClass: batchesForResultSheetQuery['batch'][0]['batch_classes'][0];
  selectedSubject: batchSubjectConfigurationForResultSheetQuery['batch_subject_configuration'][0];
}

export const ResultSheetStudents =observer(
  ({ selectedBatchClass, selectedSubject }: ResultSheetStudentsProps) => {
  const { resultSheet, user } = useMst();

  const [isRemarksModalOpen, setIsRemarksModalOpen] = useState<boolean>(false);
  const [selectedStudentResultSheet, setSelectedStudentResultSheet] = useState<StudentResultSheetModelType>();
  const [draftIsLoading, setDraftIsLoading] = useState<boolean>(false);
  const [submitIsLoading, setSubmitIsLoading] = useState<boolean>(false);
  const [approveIsLoading, setApproveIsLoading] = useState<boolean>(false);
  const [rejectIsLoading, setRejectIsLoading] = useState<boolean>(false);
  const [academicHeadRemark, setAcademicHeadRemark] = useState<string | null>(null);
  const [isDataLoading, setIsDataLoading] = useState<boolean>(false);
  const router = useRouter();

  const { id: resultSheetId } = router.query;

  const { addToast } = useToasts();

  const { clearState, studentResultSheets, loadData } = useMst().resultSheet;

  const resultSheetEditable = resultSheet.isOwnResultSheet && resultSheet.status !== 'approved';

  // Clear the mobx timetable state at mount and unmount
  useEffect(() => {
    if (!resultSheetId) {
      clearState();
    }

    return () => {
      clearState();
    };
  }, [clearState]);

  let batchSubjectConfigurationVariables: batchSubjectConfigurationForResultSheetQueryVariables;

  if (resultSheetId) {
    batchSubjectConfigurationVariables = {
      where: {
        result_sheets: {
          id: {
            _eq: resultSheetId,
          },
        },
      },
    };
  }

  const { data: subjectData } = useQuery<
    batchSubjectConfigurationForResultSheetQuery,
    batchSubjectConfigurationForResultSheetQueryVariables
  >(BATCH_SUBJECT_CONFIGURATION_FOR_RESULT_SHEET, {
    variables: batchSubjectConfigurationVariables,
    fetchPolicy:'no-cache',
  });
  useEffect(() => {
    if ((selectedSubject && selectedBatchClass) || resultSheetId) {
      loadData({
        batchClassId: selectedBatchClass?.id,
        selectedSubject:selectedSubject ?? subjectData?.batch_subject_configuration[0],
        addToast,
        setIsDataLoading,
        resultSheetId: resultSheetId as string,
        userId: user.id,
      });
    }
  }, [selectedBatchClass, selectedSubject, resultSheetId, subjectData]);

  const columns = [
    {
      title: 'Reg No',
      dataIndex: 'regNumber',
      key: 'regNumber',
      render: (_, record) => {
        return (
          <>
            <Tag>{record.regNumber}</Tag>
          </>
        );
      },
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: 'Attendance',
      dataIndex: 'attendance',
      key: 'attendance',
    },
    {
      title: 'Total',
      dataIndex: 'calculatedTotal',
      key: 'calculatedTotal',
      render: (_, record: StudentResultSheetModelType) => {
        const studentResultSheet = studentResultSheets.find(
          (studentResultSheetData) => studentResultSheetData.id === record.id,
        );
        const studentResultWithGrade = getStudentResultWithGrade(studentResultSheet.calculatedTotal);

        if (studentResultSheet) {
          return <span>{studentResultWithGrade}</span>;
        }

        return <span>-</span>;
      },
    },
    {
      title: 'Custom Total',
      dataIndex: 'totalMarks',
      key: 'totalMarks',
      width: 200,
      render: (_, record: StudentResultSheetModelType) => {
        const studentResultSheet = studentResultSheets.find(
          (studentResultSheetData) => studentResultSheetData.id === record.id,
        );

        return (
          <Input
            min={0}
            value={record.customMarks}
            onChange={(event) => {
              studentResultSheet.updateTotalMarks(parseInt(event.target.value, 10));
            }}
            type="number"
            disabled={!resultSheetEditable}
          />
        );
      },
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 100,
      fixed: 'right' as const,
      render: (_, record: StudentResultSheetModelType) => (
        <span>
          <Button
            type={record.remarks ? 'primary' : 'default'}
            size="small"
            onClick={() => {
              const studentResultSheet = studentResultSheets.find(
                (studentResultSheetData) => studentResultSheetData.id === record.id,
              );

              setSelectedStudentResultSheet(studentResultSheet);
              setIsRemarksModalOpen(true);
            }}
          >
            Remarks
          </Button>
        </span>
      ),
    },
  ];

  const studentResults = toJS(studentResultSheets);

  return (
    <>
      <Table
        loading={isDataLoading}
        bordered
        rowKey="id"
        dataSource={[...studentResults]}
        columns={columns}
        expandable={{
          expandedRowRender: (props, index, indent, expaned) => {
            // eslint-disable-next-line react/jsx-props-no-spreading
            return expaned ? <StudentResults {...props} /> : <div />;
          },
        }}
        pagination={false}
      />
      {(user.userRoles.includes('academicHead') || resultSheet.academicHeadRemark) && (
        <>
          <br />

          <Divider />

          <FormItem labelCol={{ span: 24 }} label="Academic head remark">
            <TextArea
              disabled={!user.userRoles.includes('academicHead')}
              rows={4}
              value={academicHeadRemark || resultSheet.academicHeadRemark}
              onChange={(event) => {
                setAcademicHeadRemark(event.target.value);
              }}
            />
          </FormItem>
        </>
      )}

      <br />

      <div style={{ textAlign: 'right' }}>
        {user.userRoles.includes('academicHead') && (
          <>
            <Button
              size="large"
              type="primary"
              danger
              ghost
              disabled={resultSheet.status === 'notApproved'}
              loading={rejectIsLoading}
              onClick={async () => {
                await approveDenyResultSheet({
                  resultSheetId: resultSheetId as string,
                  isApproved: false,
                  academicHeadRemark,
                  addToast,
                  setIsLoading: setRejectIsLoading,
                });
              }}
            >
              Reject
            </Button>
            &nbsp; &nbsp;
            <Button
              size="large"
              type="primary"
              disabled={resultSheet.status === 'approved'}
              loading={approveIsLoading}
              onClick={async () => {
                await approveDenyResultSheet({
                  resultSheetId: resultSheetId as string,
                  isApproved: true,
                  academicHeadRemark,
                  addToast,
                  setIsLoading: setApproveIsLoading,
                });
              }}
            >
              Approve
            </Button>
          </>
        )}

        {resultSheetEditable && (
          <>
            <Button
              size="large"
              type="primary"
              ghost
              id="result-sheet-save"
              disabled={resultSheet.status !== 'draft'}
              loading={draftIsLoading}
              onClick={async () => {
                setDraftIsLoading(true);

                await submitResultSheet({
                  resultSheet,
                  batchClassId: selectedBatchClass.id,
                  batchSubjectConfigurationId: selectedSubject.id,
                  addToast,
                });

                setDraftIsLoading(false);

                loadData({
                  batchClassId: selectedBatchClass.id,
                  selectedSubject,
                  addToast,
                  setIsDataLoading,
                  resultSheetId: resultSheetId as string,
                  userId: user.id,
                });
              }}
            >
              Save as Draft
            </Button>
            &nbsp; &nbsp;
            <Button
              id="result-sheet-submit"
              size="large"
              type="primary"
              loading={submitIsLoading}
              onClick={async () => {
                setSubmitIsLoading(true);

                await submitResultSheet({
                  resultSheet,
                  batchClassId: selectedBatchClass.id,
                  batchSubjectConfigurationId: selectedSubject.id,
                  addToast,
                  submit: true,
                });

                setSubmitIsLoading(false);

                loadData({
                  selectedSubject,
                  batchClassId: selectedBatchClass.id,
                  addToast,
                  setIsDataLoading,
                  resultSheetId: resultSheetId as string,
                  userId: user.id,
                });
              }}
            >
              {resultSheet.status === 'pending' ? 'Save & Submit' : 'Submit'}
            </Button>
          </>
        )}
      </div>

      <Modal
        title="Remarks"
        visible={isRemarksModalOpen}
        footer={
          <div>
            <Button
              onClick={() => {
                setIsRemarksModalOpen(false);
              }}
            >
              Close
            </Button>
          </div>
        }
        onCancel={() => {
          setIsRemarksModalOpen(false);
        }}
      >
        <Input.TextArea
          value={selectedStudentResultSheet?.remarks}
          onChange={(event) => {
            selectedStudentResultSheet.updateRemarks(event.target.value);
          }}
          disabled={!resultSheetEditable}
        />
      </Modal>
    </>
  );
}
)
